import React from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Layers } from 'lucide-react';
import { CVData, SectionKey } from '../../types/cv';
import {
    SECTION_LABELS,
    getItemPage,
    getSectionItems,
} from '../../utils/cvPagination';

const SECTIONS: SectionKey[] = [
    'summary',
    'experience',
    'education',
    'skills',
    'languages',
    'interests',
    'references',
];

export type OrganizerDragPayload =
    | { kind: 'section'; section: SectionKey }
    | { kind: 'item'; itemId: string; section: SectionKey };

interface PageOrganizerPanelProps {
    data: CVData;
    pageCount: number;
    onMoveItem?: (itemId: string, pageIndex: number) => void;
    onMoveSection?: (section: SectionKey, pageIndex: number) => void;
}

const DraggableRow: React.FC<{
    id: string;
    payload: OrganizerDragPayload;
    label: string;
    pageIndex: number;
    pageCount: number;
    nested?: boolean;
    onAssignPage?: (pageIndex: number) => void;
}> = ({ id, payload, label, pageIndex, pageCount, nested, onAssignPage }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id,
        data: payload,
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.35 : 1,
        touchAction: 'none' as const,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-center gap-1.5 rounded-lg border bg-white px-1.5 py-1.5 text-left shadow-sm ${
                nested ? 'ml-2 border-slate-100' : 'border-slate-200'
            } ${isDragging ? 'ring-2 ring-indigo-400' : ''}`}
        >
            <button
                type="button"
                className="shrink-0 p-1 rounded cursor-grab active:cursor-grabbing text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
                title="Glisser vers une page"
                {...listeners}
                {...attributes}
            >
                <GripVertical className="w-3.5 h-3.5" />
            </button>
            <div className="min-w-0 flex-1">
                <p
                    className={`truncate ${
                        nested ? 'text-xs text-slate-600' : 'text-xs font-semibold text-slate-800'
                    }`}
                >
                    {label}
                </p>
            </div>
            <div className="flex items-center gap-0.5 shrink-0">
                {Array.from({ length: Math.max(pageCount, 2) }, (_, i) => (
                    <button
                        key={i}
                        type="button"
                        title={`Mettre en page ${i + 1}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onAssignPage?.(i);
                        }}
                        className={`min-w-[1.35rem] h-5 px-1 rounded text-[10px] font-bold ${
                            pageIndex === i
                                ? 'bg-indigo-600 text-white'
                                : 'bg-slate-100 text-slate-500 hover:bg-indigo-100 hover:text-indigo-700'
                        }`}
                    >
                        {i + 1}
                    </button>
                ))}
            </div>
        </div>
    );
};

const PageDropZone: React.FC<{ pageIndex: number }> = ({ pageIndex }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: `page-drop-${pageIndex}`,
        data: { pageIndex },
    });

    return (
        <div
            ref={setNodeRef}
            className={`rounded-lg border-2 border-dashed px-2 py-3 text-center transition-colors ${
                isOver
                    ? 'border-indigo-500 bg-indigo-50 scale-[1.02]'
                    : 'border-slate-300 bg-slate-50'
            }`}
        >
            <p className="text-xs font-bold text-slate-700">Page {pageIndex + 1}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Déposer ici</p>
        </div>
    );
};

const PageOrganizerPanel: React.FC<PageOrganizerPanelProps> = ({
    data,
    pageCount,
    onMoveItem,
    onMoveSection,
}) => {
    const pages = Math.max(pageCount, 2);

    return (
        <aside className="w-60 shrink-0 border-l border-slate-200 bg-white/95 backdrop-blur-sm flex flex-col overflow-hidden print:hidden">
            <div className="px-3 py-3 border-b border-slate-100 flex items-center gap-2">
                <Layers className="w-4 h-4 text-indigo-600" />
                <div>
                    <p className="text-xs font-bold text-slate-800">Organisation</p>
                    <p className="text-[10px] text-slate-400">
                        Glissez vers une page, ou cliquez 1 / 2
                    </p>
                </div>
            </div>

            <div className="sticky top-0 z-10 bg-white border-b border-slate-100 p-3 space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Zones de dépôt
                </p>
                <div className="grid grid-cols-2 gap-2">
                    {Array.from({ length: pages }, (_, i) => (
                        <PageDropZone key={i} pageIndex={i} />
                    ))}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Contenu
                </p>
                {SECTIONS.map((section) => {
                    const items = getSectionItems(data, section).filter((i) => i.id);
                    if (section !== 'summary' && items.length === 0) return null;
                    if (section === 'summary' && !data.personalInfo.summary) return null;

                    const sectionPage = data.layout
                        ? getItemPage(data.layout, items[0]?.id || 'summary', section)
                        : 0;

                    return (
                        <div key={section} className="space-y-1.5">
                            <DraggableRow
                                id={`section-${section}`}
                                payload={{ kind: 'section', section }}
                                label={SECTION_LABELS[section]}
                                pageIndex={sectionPage}
                                pageCount={pages}
                                onAssignPage={(p) => onMoveSection?.(section, p)}
                            />
                            {section !== 'summary' &&
                                items.map((item) => {
                                    const page = getItemPage(data.layout, item.id, section);
                                    return (
                                        <DraggableRow
                                            key={item.id}
                                            id={`item-${item.id}`}
                                            payload={{
                                                kind: 'item',
                                                itemId: item.id,
                                                section,
                                            }}
                                            label={item.label}
                                            pageIndex={page}
                                            pageCount={pages}
                                            nested
                                            onAssignPage={(p) => onMoveItem?.(item.id, p)}
                                        />
                                    );
                                })}
                        </div>
                    );
                })}
            </div>
        </aside>
    );
};

export default PageOrganizerPanel;
